import { useRef, useCallback, useLayoutEffect } from 'react';

interface UseInfiniteScrollProps {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
}

const useInfiniteScroll = ({ hasMore, isLoading, onLoadMore }: UseInfiniteScrollProps) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  // Prevents immediate re-fire when a new last element is assigned while already in viewport.
  // Reset only on !isIntersecting (element left viewport) — NOT in useLayoutEffect —
  // because the observer callback fires after useLayoutEffect and would see an already-cleared flag.
  const isTriggeredRef = useRef(false);

  useLayoutEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  });

  const lastItemRef = useCallback(
    (node: HTMLElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) {
          isTriggeredRef.current = false;
          return;
        }
        if (hasMore && !isTriggeredRef.current) {
          isTriggeredRef.current = true;
          onLoadMoreRef.current();
        }
      });

      if (node) observer.current.observe(node);
    },
    [hasMore, isLoading]
  );

  return { lastItemRef };
};

export default useInfiniteScroll;
